/**
 * What a handset is issued with, whatever the day: the roster it signs in
 * against, the outreach site it serves, the protocol passages it can search and
 * the shapes of the data the registry and the Union Parishad list provide.
 *
 * No child is in this file. The children a device knows about — the registry
 * extract, the household list, the records already synced — arrive as a
 * `Dataset` (see dataset.ts). A production build issues an empty one; the tests
 * use the fabricated one in fixtures/demo.ts, which the build leaves out.
 */
import type { ProtocolPassage } from './retrieval.js';

export const ORG_UNIT = 'BD/CTG/PTA/KOLAGAON';

/**
 * A worker as issued to the device.
 *
 * The PIN is held only as a salted PBKDF2-SHA256 hash, never in the clear. Be
 * precise about what that buys: a four-digit PIN has ten thousand possible
 * values, so anyone holding this file can recover it offline in minutes no
 * matter how it is hashed. Hashing keeps the PIN out of plain sight in the
 * bundle and the source maps; it is hygiene, not protection. The protection in
 * a real deployment is the handset's own lock screen, a server-issued
 * credential, and the server-side lockout on repeated failures.
 */
export interface ReferenceWorker {
  id: string; code: string; pinHash: string; name: string; nameBn: string;
  designation: string; role: 'health_assistant' | 'supervisor'; scope: string; orgUnit: string;
}

export const WORKERS: ReferenceWorker[] = [
  {
    id: 'HA-042', code: 'HA-042',
    pinHash: 'pbkdf2-sha256$210000$b0cy10MBp5lXnkne-go6oQ$h6v9db_eBhYd1yonYM6TnttFvWLnsQ4QYdteNmJ3_Q0',
    name: 'Safwat Shabib', nameBn: 'সাফওয়াত শাবিব',
    designation: 'Health Assistant', role: 'health_assistant',
    scope: 'Kolagaon Union, Patiya · Wards 2, 4, 7, 9', orgUnit: ORG_UNIT
  },
  {
    id: 'SUP-07', code: 'SUP-07',
    pinHash: 'pbkdf2-sha256$210000$42uU9JRO_7qINwE6-xMGhw$KCslMAtWaYLQuuP6hpd3E7vHooZNineE9AkVcG1NNhE',
    name: 'Prerona Arman', nameBn: 'প্রেরণা আরমান',
    designation: 'EPI Supervisor', role: 'supervisor',
    scope: 'Patiya Upazila · all unions', orgUnit: 'BD/CTG/PTA'
  }
];

/** The outreach site this handset serves. A session is held here each day there is one. */
export const SITE = {
  site: 'Kolagaon EPI Outreach Centre',
  siteBn: 'কোলাগাঁও ইপিআই আউটরিচ কেন্দ্র',
  union: 'Kolagaon',
  upazila: 'Patiya',
  district: 'Chattogram',
  orgUnit: ORG_UNIT,
  planned: 30
};

/**
 * The fields of a child's record, in the order the national EPI card prints them
 * (ইপিআই টিকাদান কার্ড (শিশু), revised December 2016).
 *
 * Two are not on the card at all, and are never "read": the e-Tracker ID, which
 * the card does not carry — the child is matched to the registry by searching,
 * or enrolled as new — and the caregiver's mobile, which the worker asks for.
 * The only phone number printed on the card is the health assistant's own.
 */
export interface FieldDefinition {
  key: string; label: string; labelBn: string;
  onCard: boolean;
  /** Must be filled when a card is typed in by hand: what identifies the child. */
  required?: boolean;
  type?: 'date';
  /** Where a field that is not on the card comes from. */
  hint?: string;
}

export const FIELD_DEFINITIONS: FieldDefinition[] = [
  { key: 'regNo',      label: 'Registration no.',          labelBn: 'রেজিস্ট্রেশন নং',          onCard: true, required: true },
  { key: 'regDate',    label: 'Registration date',         labelBn: 'রেজিস্ট্রেশনের তারিখ',      onCard: true, type: 'date' },
  { key: 'childName',  label: "Child's name",              labelBn: 'শিশুর নাম',                onCard: true, required: true },
  { key: 'sex',        label: 'Sex',                       labelBn: 'ছেলে/মেয়ে',                onCard: true, required: true },
  { key: 'dob',        label: 'Date of birth',             labelBn: 'জন্ম তারিখ',               onCard: true, required: true, type: 'date' },
  { key: 'birthRegNo', label: 'Birth registration no.',    labelBn: 'জন্ম নিবন্ধন সার্টিফিকেট নং', onCard: true },
  { key: 'motherName', label: "Mother's name",             labelBn: 'মাতার নাম',                onCard: true },
  { key: 'fatherName', label: "Father's name",             labelBn: 'পিতার নাম',                onCard: true },
  { key: 'village',    label: 'Village / mohalla / para',  labelBn: 'গ্রাম/মহল্লা/পাড়া',       onCard: true },
  { key: 'ward',       label: 'Ward',                      labelBn: 'ওয়ার্ড নং',                onCard: true },
  { key: 'union',      label: 'Union / zone',              labelBn: 'ইউনিয়ন/জোন',              onCard: true },
  { key: 'upazila',    label: 'Upazila / municipality',    labelBn: 'উপজেলা/পৌরসভা',           onCard: true },
  { key: 'centre',     label: 'Vaccination centre',        labelBn: 'কেন্দ্রের নাম',             onCard: true },
  { key: 'epiId',      label: 'e-Tracker ID',              labelBn: 'ই-ট্র্যাকার আইডি',          onCard: false, hint: 'Not on the card. Filled when you find the child in the registry.' },
  { key: 'phone',      label: 'Caregiver mobile',          labelBn: 'অভিভাবকের মোবাইল',         onCard: false, hint: 'Not on the card. Ask the caregiver, for reminders.' }
];

export const FIELD_BY_KEY: Record<string, FieldDefinition> = Object.fromEntries(FIELD_DEFINITIONS.map(f => [f.key, f]));


export interface RegistryChild {
  epiId: string; name: string; nameBn: string; motherName: string;
  village: string; ward: number; dob: string;
}

export interface HouseholdChild {
  id: string; name: string; nameBn: string; sex: 'Male' | 'Female'; dob: string;
  etrackerId: string | null; note?: string;
}

export interface Household {
  id: string; head: string; headBn: string; village: string; ward: number; listRef: string;
  children: HouseholdChild[];
}

export const PROTOCOL_PASSAGES: ProtocolPassage[] = [
  { id: 'P-01', topic: 'MR-1 age',
    bn: 'এমআর প্রথম ডোজ শিশুর ৯ মাস বয়স পূর্ণ হলে দিতে হবে। দ্বিতীয় ডোজ ১৫ মাস বয়সে।',
    ctg: 'এমআর-র পয়লা ডোজ পোয়ার ৯ মাস অইলে দিয়ন পড়িব। দ্বিতীয় ডোজ ১৫ মাসত।',
    en: 'MR dose 1 is given at completed 9 months of age; dose 2 at 15 months.',
    source: 'DGHS EPI Field Guide · Routine schedule table',
    terms: 'measles mr mr1 rubella nine months first dose age when schedule হাম এমআর কখন বয়স' },
  { id: 'P-02', topic: 'Minimum interval',
    bn: 'একই টিকার দুই ডোজের মাঝে কমপক্ষে ২৮ দিন ব্যবধান রাখতে হবে। আগে দিলে ডোজটি গণনা হবে না।',
    ctg: 'একই টিকার দুই ডোজর মাঝে কমছে কম ২৮ দিন থাইকন পড়িব। আগে দিলে ঐ ডোজ গোনা ন যাইব।',
    en: 'Keep at least 28 days between two doses of the same antigen. A dose given earlier is not counted.',
    source: 'DGHS EPI Field Guide · Minimum intervals',
    terms: 'interval gap minimum between penta pentavalent 28 days next dose too soon early কতদিন ব্যবধান' },
  { id: 'P-03', topic: 'Delayed schedule',
    bn: 'দেরি হলে সিরিজ নতুন করে শুরু করা যাবে না। যেখানে থেমেছে সেখান থেকে বাকি ডোজ দিতে হবে।',
    ctg: 'দেরি অইলে নতুন গরি শুরু গরন ন পড়িব। যেন্ডে থামি গেছে, হেন্ডততুন বাকি ডোজ দিয়ন পড়িব।',
    en: 'Do not restart an interrupted series. Continue from where it stopped and complete the remaining doses.',
    source: 'WHO Immunization in Practice · Module 5',
    terms: 'late missed miss overdue restart start over behind interrupted catch up defaulter দেরি বাদ শুরু' },
  { id: 'P-04', topic: 'VVM stages',
    bn: 'ভিভিএম-এর ভেতরের বর্গ বাইরের বৃত্তের চেয়ে হালকা থাকলে (স্টেজ ১ ও ২) টিকা ব্যবহারযোগ্য। সমান বা গাঢ় হলে (স্টেজ ৩ ও ৪) ব্যবহার করা যাবে না।',
    ctg: 'ভিভিএম-র ভিতরর চৌকোনা বাইরর গোলর তুন হালকা অইলে (স্টেজ ১-২) টিকা চলিব। সমান বা গাঢ় অইলে (স্টেজ ৩-৪) ন চলিব।',
    en: 'Usable while the inner square is lighter than the outer ring (stages 1–2). Discard when it matches or is darker (stages 3–4).',
    source: 'UNICEF/WHO VVM guidance · Reading the monitor',
    terms: 'vvm vial monitor stage square ring dark darker usable discard throw ভায়াল মনিটর স্টেজ' },
  { id: 'P-05', topic: 'Storage temperature',
    bn: 'টিকা ২–৮ ডিগ্রি সেলসিয়াসে রাখতে হবে। পেন্টা ও পিসিভি কখনো জমাট বাঁধতে দেওয়া যাবে না।',
    ctg: 'টিকা ২–৮ ডিগ্রিত রাখন পড়িব। পেন্টা আর পিসিভি কোনোদিন জমাট বান্ধন ন দিয়ন।',
    en: 'Store vaccines at 2–8°C. Penta and PCV must never be allowed to freeze.',
    source: 'DGHS Cold Chain Handbook · Storage',
    terms: 'temperature storage store cold chain fridge freeze frozen ice carrier degrees তাপমাত্রা ঠান্ডা জমাট' },
  { id: 'P-06', topic: 'Mild illness',
    bn: 'হালকা জ্বর, সর্দি বা ডায়রিয়া টিকা দেওয়ার প্রতিবন্ধকতা নয়; শিশুকে টিকা দেওয়া যাবে।',
    ctg: 'হালকা জ্বর, সর্দি বা পাতলা পায়খানা অইলেও টিকা দিয়ন যাইব; এগুন বাধা ন।',
    en: 'Mild fever, cold or diarrhoea are not contraindications; the child can be vaccinated.',
    source: 'WHO Immunization in Practice · Contraindications',
    terms: 'fever sick ill illness cold cough diarrhoea unwell contraindication safe vaccinate জ্বর অসুস্থ সর্দি' },
  { id: 'P-07', topic: 'Lost EPI card',
    bn: 'কার্ড হারালে ই-ট্র্যাকারে শিশুর রেকর্ড দেখে নতুন কার্ড ইস্যু করতে হবে, ইতিহাস অনুমান করা যাবে না।',
    ctg: 'কার্ড হারাইলে ই-ট্র্যাকারত পোয়ার রেকর্ড চাই নতুন কার্ড দিয়ন পড়িব; ইতিহাস আন্দাজ গরন ন যাইব।',
    en: 'If the card is lost, issue a new one from the e-Tracker record. Never estimate dose history.',
    source: 'DGHS EPI Field Guide · Record keeping',
    terms: 'card lost missing duplicate replacement new card without কার্ড হারিয়ে হারাইলে' },
  { id: 'P-08', topic: 'AEFI reporting',
    bn: 'টিকা পরবর্তী গুরুতর প্রতিক্রিয়া ২৪ ঘণ্টার মধ্যে উপজেলা স্বাস্থ্য ও পরিবার পরিকল্পনা কর্মকর্তাকে জানাতে হবে।',
    ctg: 'টিকার পরে গুরুতর কিছু অইলে ২৪ ঘণ্টার ভিতরে উপজিলার স্বাস্থ্য কর্মকর্তারে জানান পড়িব।',
    en: 'Report any serious adverse event following immunization to the UH&FPO within 24 hours.',
    source: 'DGHS AEFI surveillance · Reporting line',
    terms: 'aefi reaction adverse side effect swelling abscess report serious পার্শ্ব প্রতিক্রিয়া' },
  { id: 'P-09', topic: 'Open vial policy',
    bn: 'খোলা বহু-ডোজ ভায়াল সেশন শেষে ফেলে দিতে হবে যদি তা আউটরিচ সেশনে খোলা হয়ে থাকে।',
    ctg: 'আউটরিচ সেশনত খোলা বহু-ডোজ ভায়াল সেশন শেষত ফেলাই দিয়ন পড়িব।',
    en: 'A multi-dose vial opened at an outreach session is discarded at the end of that session.',
    source: 'WHO Multi-dose vial policy · Outreach',
    terms: 'open vial opened multi dose leftover end of session keep reuse খোলা ভায়াল কতক্ষণ' },
  { id: 'P-10', topic: 'No BCG scar',
    bn: 'বিসিজি-র দাগ না উঠলেও রুটিন হিসেবে পুনরায় বিসিজি দেওয়া যাবে না।',
    ctg: 'বিসিজির দাগ ন উঠিলেও রুটিন হিসাবে আরেকবার বিসিজি দিয়ন ন যাইব।',
    en: 'Absence of a BCG scar is not by itself a reason to repeat BCG.',
    source: 'WHO Immunization in Practice · BCG',
    terms: 'bcg scar mark no scar repeat revaccinate দাগ বিসিজি' },
  { id: 'P-11', topic: 'Zero-dose child found',
    bn: 'কোনো ডোজ না পাওয়া শিশু পাওয়া গেলে বয়স অনুযায়ী ক্যাচ-আপ সূচি শুরু করে ই-ট্র্যাকারে নিবন্ধন করতে হবে।',
    ctg: 'কোনো ডোজ ন পাওয়া পোয়া পাইলে বয়স অনুযায়ী ক্যাচ-আপ শুরু গরি ই-ট্র্যাকারত নিবন্ধন গরন পড়িব।',
    en: 'When a child with no doses is found, start an age-appropriate catch-up schedule and enrol them in the e-Tracker.',
    source: 'DGHS EPI Field Guide · Defaulter tracing',
    terms: 'zero dose never vaccinated no dose unregistered not registered register enrol found child টিকা পায়নি নিবন্ধন' }
];

export interface ZeroDoseCaseSeed {
  childId: string; status: 'assigned' | 'closed'; outcome: string | null;
  assignedTo: string | null; note: string | null; updatedAt: string;
}

export interface VvmSeedReview {
  id: string; reviewedAt: string; antigen: string; batch: string;
  assistedStage: 1 | 2 | 3 | 4; assistedConfidence: number; workerStage: 1 | 2 | 3 | 4;
  agreed: boolean; action: string; workerId: string;
}

export const VACCINE_PRESENTATIONS = [
  'Penta (10-dose)', 'MR (10-dose)', 'PCV (4-dose)', 'OPV (20-dose)', 'BCG (20-dose)'
];
